import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ExternalLink, Heart } from "lucide-react";
import { App } from "../../types";
import { createCardVariants } from "./animations";
import {
  isValidAppUrl,
  isValidUserExternalUrl,
} from "../../utils/urlValidator";
import logger from "../../utils/logger";

interface AppCardProps {
  /** Application data */
  app: App;
  /** Card index for stagger animation */
  index: number;
  /** If true, URL is validated with isValidUserExternalUrl (any host) for My Apps */
  isUserCustomApp?: boolean;
  /** Whether the app is marked as favorite by the current user */
  isFavorite?: boolean;
  /** Called when the user clicks the favorite heart (optional) */
  onToggleFavorite?: () => void;
  /** Hide the top-right action area (heart + external icon) — used when the parent renders its own actions */
  hideTopActions?: boolean;
  /** Whole card can be dragged to reorder. Clicks still open the app. */
  isDraggable?: boolean;
}

/** Default app color (matches theme primary-500 / --color-primary-500) */
const DEFAULT_COLOR = "#c00035";

const INTERNAL_ROUTE_PREFIXES = [
  "hub-management",
  "haat-requests",
  "taskify-management",
  "campaigns-management",
  "devices-management",
  "careers-management",
  "partner-msg-management",
  "product-team",
] as const;

const isInternalAppUrl = (appUrl: string): boolean => {
  const url = (appUrl ?? "").trim();
  if (!url) return false;
  if (/^https?:\/\//i.test(url)) return false;
  if (url.startsWith("/")) return true;
  return INTERNAL_ROUTE_PREFIXES.some(
    (prefix) => url === prefix || url.startsWith(`${prefix}/`),
  );
};

const toInternalPath = (appUrl: string): string => {
  const url = (appUrl ?? "").trim();
  if (!url) return "/dashboard";
  return url.startsWith("/") ? url : `/${url}`;
};

const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const AppCard: React.FC<AppCardProps> = ({
  app,
  index,
  isUserCustomApp = false,
  isFavorite = false,
  onToggleFavorite,
  hideTopActions = false,
  isDraggable = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("dashboard");
  const cardVariants = createCardVariants(index);
  const appColor = app.appColor || DEFAULT_COLOR;
  const appUrlTrimmed = (app.appUrl ?? "").trim();

  const isHaatRequestsApp =
    appUrlTrimmed === "haat-requests" ||
    appUrlTrimmed.startsWith("haat-requests/") ||
    appUrlTrimmed.startsWith("/haat-requests");
  const appDisplayName = isHaatRequestsApp ? "HCRM" : app.appName;

  const isInternal = isInternalAppUrl(app.appUrl);

  const initials = useMemo(() => getInitials(appDisplayName), [appDisplayName]);

  const handleClick = useCallback(() => {
    if (isInternal) {
      navigate(toInternalPath(app.appUrl));
    } else {
      const isValid = isUserCustomApp
        ? isValidUserExternalUrl(app.appUrl)
        : isValidAppUrl(app.appUrl);
      if (!isValid) {
        logger.error(
          `Blocked navigation to invalid URL for app: ${app.appName}`,
        );
        return;
      }
      window.open(app.appUrl, "_blank", "noopener,noreferrer");
    }
  }, [app.appUrl, app.appName, isInternal, isUserCustomApp, navigate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const handleFavoriteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onToggleFavorite?.();
    },
    [onToggleFavorite],
  );

  return (
    <motion.article
      variants={cardVariants}
      whileHover={{ boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`Open ${appDisplayName}${!isInternal ? " (opens in new tab)" : ""}`}
      className={`bg-white shadow-sm transition-all duration-200 group relative overflow-hidden focus:outline-none focus:ring-2 focus:ring-haat-red focus:ring-offset-2 h-full min-h-[150px] ${
        isDraggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
      }`}
    >
      {/* Thin top accent bar */}
      <div
        className="absolute top-0 left-0 right-0 h-1 transition-all duration-200 group-hover:h-1.5"
        style={{ backgroundColor: appColor }}
        aria-hidden="true"
      />

      {/* Top-right actions */}
      {!hideTopActions && (
        <div className="absolute top-2.5 right-2.5 flex items-center gap-1">
          {onToggleFavorite != null && (
            <motion.button
              type="button"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={handleFavoriteClick}
              className="p-1 rounded-md text-gray-300 hover:text-haat-red focus:outline-none focus:ring-1 focus:ring-haat-red transition-colors"
              aria-label={
                isFavorite
                  ? t("favorite.removeFromFavorites")
                  : t("favorite.addToFavorites")
              }
              whileTap={{ scale: 0.85 }}
            >
              <Heart
                size={14}
                className={
                  isFavorite
                    ? "fill-haat-red text-haat-red"
                    : "group-hover:text-gray-400"
                }
              />
            </motion.button>
          )}
          {!isInternal && (
            <ExternalLink
              size={12}
              className="text-gray-300 group-hover:text-gray-400 transition-colors"
            />
          )}
        </div>
      )}

      {/* Content */}
      <div className="p-4 pt-5">
        {/* Avatar */}
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 overflow-hidden flex-shrink-0"
          style={{ backgroundColor: `${appColor}18` }}
          aria-hidden="true"
        >
          {app.appImageUrl ? (
            <img
              src={app.appImageUrl}
              alt={app.appName}
              className="w-full h-full object-cover rounded-xl"
            />
          ) : (
            <span
              className="text-xs font-bold tracking-tight"
              style={{ color: appColor }}
            >
              {initials}
            </span>
          )}
        </div>

        {/* Name */}
        <h3 className="text-sm font-semibold text-gray-900 line-clamp-1 group-hover:text-haat-red transition-colors duration-150 mb-1">
          {appDisplayName}
        </h3>

        {/* Description — only render when present */}
        {app.appDescription ? (
          <p className="text-xs text-gray-400 line-clamp-2 leading-relaxed">
            {app.appDescription}
          </p>
        ) : null}
      </div>

      {/* Subtle hover gradient overlay */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-[0.03] transition-opacity duration-200 pointer-events-none"
        style={{
          background: `linear-gradient(135deg, ${appColor}, transparent)`,
        }}
        aria-hidden="true"
      />
    </motion.article>
  );
};

export default AppCard;
